import type { IBulletChartConfig, IBulletChartBuild } from './types';

import { merge } from '../../helper';

import addClass from '../../general/class/addClass';

import * as exElement from '../../utils/element';

import { toPercent, isInRange, getNormalRange, resolveScale, resolveRanges, resolveRangeLabel } from './utils';

// ----------------------------------------------------------------------------

type TypeMethodsConfig = {
    min: number;
    max: number;
};

// ----------------------------------------------------------------------------

function create(config: IBulletChartConfig = {}): IBulletChartBuild {
    const defConfig: IBulletChartConfig = {};
    merge(defConfig, config);

    const { min, max } = resolveScale(defConfig);

    // ------------------------------------------------------------------------

    const build = {
        elements: {
            ranges: [],
            label: null,
            rangeLabel: null,
        },
    } as unknown as IBulletChartBuild;

    // ------------------------------------------------------------------------

    const elWrapper = exElement.create({
        merge: defConfig,
        class: {
            new: 'bullet-chart',
        },
    });
    build.element = elWrapper;
    build.elements.wrapper = elWrapper;

    if (defConfig.vertical === true) {
        addClass(elWrapper, 'bullet-chart-vertical');
    }

    // ------------------------------------------------------------------------

    if (defConfig.label != null) {
        const elLabel = exElement.create({
            merge: typeof defConfig.label === 'string' ? { html: defConfig.label } : defConfig.label,
            class: {
                new: 'bullet-chart-label',
            },
        });
        build.elements.label = elLabel;
        elWrapper.appendChild(elLabel);
    }

    // ------------------------------------------------------------------------

    const elTrack = exElement.create({
        class: {
            new: 'bullet-chart-track',
        },
    });
    build.elements.track = elTrack;
    elWrapper.appendChild(elTrack);

    // ------------------------------------------------------------------------

    for (const resolved of resolveRanges(defConfig, min, max)) {
        const elRange = exElement.create({
            merge: resolved,
            class: {
                new: 'bullet-chart-range',
            },
            style: {
                left: resolved.left + '%',
                width: resolved.width + '%',
            },
        });

        if (typeof resolved.color === 'string') {
            addClass(elRange, 'bullet-chart-range-' + resolved.color);
        }

        build.elements.ranges.push(elRange);
        elTrack.appendChild(elRange);
    }

    // ------------------------------------------------------------------------

    const elBar = exElement.create({
        class: {
            new: 'bullet-chart-bar',
        },
    });

    if (defConfig.value != null) {
        const value = defConfig.value;
        elBar.style.left = toPercent(value, min, max) + '%';

        const normalRange = getNormalRange(defConfig);

        if (normalRange != null) {
            const normal = isInRange(value, normalRange.normalMin, normalRange.normalMax);
            addClass(elBar, normal ? 'bullet-chart-bar-normal' : 'bullet-chart-bar-abnormal');
        }
    }

    build.elements.bar = elBar;
    elTrack.appendChild(elBar);

    // ------------------------------------------------------------------------

    const rangeLabelText = resolveRangeLabel(defConfig);

    if (rangeLabelText != null) {
        const elRangeLabel = exElement.create({
            html: rangeLabelText,
            class: {
                new: 'bullet-chart-range-label',
            },
        });
        build.elements.rangeLabel = elRangeLabel;
        elWrapper.appendChild(elRangeLabel);
    }

    // ------------------------------------------------------------------------

    // Methods
    methods(build, { min, max });

    // ------------------------------------------------------------------------

    return build;
}

function methods(build: IBulletChartBuild, config: TypeMethodsConfig): void {
    const { min, max } = config;
    const normalRange = getNormalRange(config);

    build.setValue = function (value: number): void {
        const percent = toPercent(value, min, max);
        build.elements.bar.style.left = percent + '%';

        if (normalRange != null) {
            const normal = isInRange(value, normalRange.normalMin, normalRange.normalMax);
            build.elements.bar.classList.remove('bullet-chart-bar-normal', 'bullet-chart-bar-abnormal');
            build.elements.bar.classList.add(normal ? 'bullet-chart-bar-normal' : 'bullet-chart-bar-abnormal');
        }
    };
}

// ----------------------------------------------------------------------------

export { create };
